import axios from 'axios';

// IMPORTANT: Note that this base URL already includes '/api'
// Do NOT include '/api' in the route paths below
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.mascottcompliance.com/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for adding auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    try {
      // Validate token before sending it
      const payload = JSON.parse(atob(token.split('.')[1]));
      const isExpired = payload.exp * 1000 < Date.now();
      
      if (isExpired) {
        console.warn('Token expired, removing from localStorage');
        localStorage.removeItem('token');
        // Don't add expired token to request
      } else {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (e) {
      console.warn('Invalid token format, removing from localStorage');
      localStorage.removeItem('token');
      // Don't add invalid token to request
    }
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Response interceptor for handling auth errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle network errors
    if (!error.response) {
      console.error('Network error - server may be down:', error.message);
      return Promise.reject(new Error('Unable to connect to server. Please check your connection and try again.'));
    }

    // Handle 401 unauthorized errors - but validate token first
    if (error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const token = localStorage.getItem('token');
      if (token) {
        try {
          // Check if token is actually expired
          const payload = JSON.parse(atob(token.split('.')[1]));
          const isExpired = payload.exp * 1000 < Date.now();
          
          if (isExpired) {
            // Token is genuinely expired - remove it and redirect
            console.log('Token expired, redirecting to login');
            localStorage.removeItem('token');
            window.location.href = '/login';
          } else {
            // Token is valid but got 401 - likely a race condition or server issue
            // Don't remove the token, just let the request fail normally
            console.warn('Got 401 with valid token - possible race condition or server issue');
          }
        } catch (e) {
          // Invalid token format - remove it
          console.log('Invalid token format, redirecting to login');
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
      } else {
        // No token at all - redirect to login
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const apiService = {
  // Authentication
  async login(credentials) {
    try {
      const response = await api.post('/auth/login', credentials);
      if (response.data.token) {
        // Clear any existing token first to prevent conflicts
        localStorage.removeItem('token');
        
        // Set new token
        localStorage.setItem('token', response.data.token);
        
        // Clear any cached data that might contain old tokens
        if (typeof window !== 'undefined' && 'caches' in window) {
          caches.keys().then(names => {
            names.forEach(name => {
              caches.delete(name);
            });
          });
        }
        
        console.log('New token set, cleared caches');
      }
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw error.response?.data?.message || 'Login failed';
    }
  },

  async setPreferredBranch(userId, branchName) {
    try {
      const response = await api.post('/auth/set-preferred-branch', { userId, branchName });
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
      }
      return response.data;
    } catch (error) {
      console.error('Set preferred branch error:', error);
      throw error.response?.data?.message || 'Failed to set preferred branch';
    }
  },

  async updateMyPreferredBranch(branchName) {
    try {
      const response = await api.post('/auth/update-my-preferred-branch', { branchName });
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
      }
      return response.data;
    } catch (error) {
      console.error('Update preferred branch error:', error);
      throw error.response?.data?.message || 'Failed to update preferred branch';
    }
  },

  async register(userData) {
    try {
      const response = await api.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      console.error('Registration error:', error);
      // Throw full error to allow callers to inspect errorType and status
      throw error;
    }
  },

  async verifyEmail(email, code) {
    try {
      const response = await api.post('/auth/verify-email', { email, code });
      return response.data;
    } catch (error) {
      console.error('Email verification error:', error);
      throw error.response?.data?.message || 'Verification failed';
    }
  },

  async resendVerificationCode(email) {
    try {
      const response = await api.post('/auth/resend-verification', { email });
      return response.data;
    } catch (error) {
      console.error('Error resending verification code:', error);
      throw error.response?.data?.message || 'Failed to resend verification code';
    }
  },

  async requestPasswordReset(email) {
    try {
      const response = await api.post('/auth/request-password-reset', { email });
      return response.data;
    } catch (error) {
      console.error('Error requesting password reset:', error);
      throw error.response?.data?.message || 'Failed to request password reset';
    }
  },

  async validatePasswordResetToken(token) {
    try {
      const response = await api.get(`/auth/validate-reset-token?token=${token}`);
      return response.data;
    } catch (error) {
      console.error('Error validating reset token:', error);
      throw error.response?.data?.message || 'Invalid or expired token';
    }
  },

  async resetPassword(token, newPassword) {
    try {
      const response = await api.post('/auth/reset-password', { token, newPassword });
      return response.data;
    } catch (error) {
      console.error('Error resetting password:', error);
      throw error.response?.data?.message || 'Failed to reset password';
    }
  },

  // User
  async getUser() {
    try {
      const response = await api.get('/user/current');
      return response.data;
    } catch (error) {
      console.error('Error fetching user data:', error);
      throw error.response?.data?.message || 'Failed to fetch user data';
    }
  },

  async getUserDashboard() {
    try {
      const response = await api.get('/user/dashboard');
      return response.data;
    } catch (error) {
      console.error('Error fetching user dashboard:', error);
      throw error.response?.data?.message || 'Failed to fetch dashboard data';
    }
  },

  async getVehicleConflictStatus() {
    try {
      const response = await api.get('/user/vehicle-conflict-status');
      return response.data;
    } catch (error) {
      console.error('Error fetching vehicle conflict status:', error);
      // Don't throw for this - return null so the UI can handle gracefully
      return null;
    }
  },

  // Admin
  async getAdminDashboardData() {
    try {
      const response = await api.get('/admin/dashboard');
      return response.data;
    } catch (error) {
      console.error('Error fetching admin dashboard data:', error);
      throw error.response?.data?.message || 'Failed to fetch dashboard data';
    }
  },

  async deleteUser(userId) {
    try {
      const response = await api.delete(`/admin/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error.response?.data?.message || 'Failed to delete user';
    }
  },

  async updateUser(userId, userData) {
    try {
      const response = await api.put(`/admin/users/${userId}`, userData);
      return response.data;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error.response?.data?.message || 'Failed to update user';
    }
  },

  async resetUserPassword(userId) {
    try {
      const response = await api.post(`/admin/users/${userId}/reset-password`);
      return response.data;
    } catch (error) {
      console.error('Error resetting user password:', error);
      throw error.response?.data?.message || 'Failed to reset user password';
    }
  },

  // Checklists
  async getChecklists(page = 1, limit = 10) {
    try {
      const response = await api.get('/checklists', {
        params: { page, limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching checklists:', error);
      throw error.response?.data?.message || 'Failed to fetch checklists';
    }
  },

  async getChecklistById(id) {
    try {
      const response = await api.get(`/checklists/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching checklist:', error);
      throw error.response?.data?.message || 'Failed to fetch checklist';
    }
  },

  async createChecklist(checklistData) {
    try {
      const response = await api.post('/checklists', checklistData);
      return response.data;
    } catch (error) {
      console.error('Error creating checklist:', error);
      throw error.response?.data?.message || 'Failed to create checklist';
    }
  },

  // Branches
  async getBranches() {
    try {
      const response = await api.get('/branches');
      return response.data;
    } catch (error) {
      console.error('Error fetching branches:', error);
      throw error.response?.data?.message || 'Failed to fetch branches';
    }
  },

  async getBranchByName(branchName) {
    try {
      const response = await api.get(`/branches/${encodeURIComponent(branchName)}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching branch:', error);
      throw error.response?.data?.message || 'Failed to fetch branch';
    }
  },

  async updateBranch(branchName, branchData) {
    try {
      const response = await api.put(`/branches/${encodeURIComponent(branchName)}`, branchData);
      return response.data;
    } catch (error) {
      console.error('Error updating branch:', error);
      throw error.response?.data?.message || 'Failed to update branch';
    }
  },

  // Email Settings
  async getEmailSettings() {
    try {
      const response = await api.get('/admin/email-lists');
      return response.data;
    } catch (error) {
      console.error('Error fetching email settings:', error);
      throw error.response?.data?.message || 'Failed to fetch email settings';
    }
  },

  async updateEmailSettings(branchName, settings) {
    try {
      const response = await api.post('/admin/email-lists', {
        branchName,
        ...settings
      });
      return response.data;
    } catch (error) {
      console.error('Error updating email settings:', error);
      throw error.response?.data?.message || 'Failed to update email settings';
    }
  },

  // Driver Report Recipients
  async getDriverReportRecipients() {
    try {
      const response = await api.get('/admin/driver-report-recipients');
      return response.data;
    } catch (error) {
      console.error('Error fetching driver report recipients:', error);
      throw error.response?.data?.message || 'Failed to fetch driver report recipients';
    }
  },

  async updateDriverReportRecipients(recipients) {
    try {
      const response = await api.post('/admin/driver-report-recipients', { recipients });
      return response.data;
    } catch (error) {
      console.error('Error updating driver report recipients:', error);
      throw error.response?.data?.message || 'Failed to update driver report recipients';
    }
  },

  async getWebPortalAllocationSettings() {
    try {
      const response = await api.get('/admin/web-portal-allocation');
      return response.data;
    } catch (error) {
      console.error('Error fetching Web Portal allocation settings:', error);
      throw error.response?.data?.message || 'Failed to fetch Web Portal allocation settings';
    }
  },

  async updateWebPortalAllocationSettings(payload) {
    try {
      const response = await api.put('/admin/web-portal-allocation', payload);
      return response.data;
    } catch (error) {
      console.error('Error updating Web Portal allocation settings:', error);
      throw error.response?.data?.message || 'Failed to update Web Portal allocation settings';
    }
  },

  // Reminders
  async sendReminderEmail(userId) {
    try {
      const response = await api.post(`/reminders/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error sending reminder email:', error);
      throw error.response?.data?.message || 'Failed to send reminder';
    }
  },

  // Safety Letters
  async uploadSafetyLetter(formData) {
    try {
      const response = await api.post('/admin/safety-letters/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading safety letter:', error);
      throw error.response?.data?.message || 'Failed to upload safety letter';
    }
  },

  async getAllSafetyLetters() {
  try {
    const response = await api.get('/admin/safety-letters');
    return response.data;
  } catch (error) {
    console.error('Error fetching safety letters:', error);
    throw error.response?.data?.message || 'Failed to fetch safety letters';
  }
},

  async getCurrentMonthSafetyLetter() {
    try {
      const response = await api.get('/user/safety-letters/current');
      return response.data;
    } catch (error) {
      console.error('Error fetching current safety letter:', error);
      throw error.response?.data?.message || 'Failed to fetch current safety letter';
    }
  },

  async signSafetyLetter(id, signature) {
    try {
      const response = await api.post(`/user/safety-letters/${id}/sign`, { signature });
      return response.data;
    } catch (error) {
      console.error('Error signing safety letter:', error);
      throw error.response?.data?.message || 'Failed to sign safety letter';
    }
  },

  async deleteSafetyLetter(id) {
    try {
      const response = await api.delete(`/admin/safety-letters/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting safety letter:', error);
      throw error.response?.data?.message || 'Failed to delete safety letter';
    }
  },

  async getSafetyLetterUrl(letterId) {
    try {
      const response = await api.get(`/admin/safety-letters/${letterId}/url`);
      return response.data.url;
    } catch (error) {
      console.error('Error getting safety letter URL:', error);
      throw error.response?.data?.message || 'Failed to get safety letter URL';
    }
  },

  // Compliance
  async getComplianceData() {
    try {
      const response = await api.get('/admin/compliance');
      return response.data;
    } catch (error) {
      console.error('Error fetching compliance data:', error);
      throw error.response?.data?.message || 'Failed to fetch compliance data';
    }
  },

  // Maintenance Invoices
  async getUserMaintenanceInvoices() {
    try {
      const response = await api.get('/maintenance-invoices');
      return response.data;
    } catch (error) {
      console.error('Error fetching maintenance invoices:', error);
      throw error.response?.data?.message || 'Failed to fetch maintenance invoices';
    }
  },

  async uploadMaintenanceInvoice(invoiceData) {
    try {
      const formData = new FormData();
      formData.append('service_provider', invoiceData.service_provider);
      formData.append('work_performed', invoiceData.work_performed);
      formData.append('file', invoiceData.file);

      const response = await api.post('/maintenance-invoices/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading maintenance invoice:', error);
      throw error.response?.data?.message || 'Failed to upload maintenance invoice';
    }
  },

  async deleteMaintenanceInvoice(id) {
    try {
      const response = await api.delete(`/maintenance-invoices/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting maintenance invoice:', error);
      throw error.response?.data?.message || 'Failed to delete maintenance invoice';
    }
  },

  // Expense Reports
  async getOrCreateMonthlyReport(params) {
    try {
      const response = await api.get('/expense-reports/monthly', { 
        params: {
          ...params,
          create_new: params.create_new || false
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting expense report:', error);
      throw error.response?.data?.message || 'Failed to get expense report';
    }
  },

  async addExpenseItem(reportId, itemData) {
    try {
      const response = await api.post(`/expense-reports/${reportId}/items`, itemData);
      return response.data;
    } catch (error) {
      console.error('Error adding expense item:', error);
      throw error.response?.data?.message || 'Failed to add expense item';
    }
  },

  async deleteExpenseItem(reportId, itemId) {
    try {
      const response = await api.delete(`/expense-reports/${reportId}/items/${itemId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting expense item:', error);
      throw error.response?.data?.message || 'Failed to delete expense item';
    }
  },


sendExpenseReportToBilling: async function(reportId, billingEmail) {
  try {
    const response = await api.post(`/expense-reports/admin/${reportId}/send-to-billing`, { 
      billingEmail 
    });
    return response.data;
  } catch (error) {
    console.error('Error sending expense report to billing:', error);
    throw error.response?.data?.message || 'Failed to send expense report to billing';
  }
},

  async uploadExpenseReceipts(reportId, formData) {
    try {
      const response = await api.post(`/expense-reports/${reportId}/receipts`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading receipts:', error);
      throw error.response?.data?.message || 'Failed to upload receipts';
    }
  },

  async deleteExpenseReceipt(reportId, receiptId) {
    try {
      const response = await api.delete(`/expense-reports/${reportId}/receipts/${receiptId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting receipt:', error);
      throw error.response?.data?.message || 'Failed to delete receipt';
    }
  },

  async submitExpenseReport(reportId) {
    try {
      const response = await api.post(`/expense-reports/${reportId}/submit`);
      return response.data;
    } catch (error) {
      console.error('Error submitting expense report:', error);
      throw error.response?.data?.message || 'Failed to submit expense report';
    }
  },


  async getExpenseReportCompliance() {
    try {
      // Add timestamp to prevent caching
      const timestamp = new Date().getTime();
      const response = await api.get(`/admin/expense-reports/compliance?_t=${timestamp}`, {
        headers: {
          'Cache-Control': 'no-cache, no-store',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching expense report compliance:', error);
      throw error.response?.data?.message || 'Failed to fetch expense report compliance';
    }
  },

  async downloadExpenseReport(reportId) {
    try {
      const response = await api.get(`/expense-reports/${reportId}/download`, {
        responseType: 'blob'
      });
      return response;
    } catch (error) {
      console.error('Error downloading expense report:', error);
      throw error.response?.data?.message || 'Failed to download expense report';
    }
  },

  // MRL (Missing Receipt Ledger)
  async addMRLItem(reportId, itemData) {
    try {
      // Make sure we're not passing temporary IDs from the client
      const { id, ...dataWithoutId } = itemData;
      const response = await api.post(`/expense-reports/${reportId}/mrl`, dataWithoutId);
      return response.data;
    } catch (error) {
      console.error('Error adding MRL item:', error);
      throw error.response?.data?.message || 'Failed to add MRL item';
    }
  },

  async addMRLItems(reportId, items) {
    try {
      // Remove any temporary IDs
      const cleanedItems = items.map(item => {
        const { id, ...itemWithoutId } = item;
        return itemWithoutId;
      });
      
      const response = await api.post(`/expense-reports/${reportId}/mrl/bulk`, { 
        items: cleanedItems 
      });
      return response.data;
    } catch (error) {
      console.error('Error adding MRL items:', error);
      throw error.response?.data?.message || 'Failed to add MRL items';
    }
  },

  async updateMRLItem(reportId, itemId, itemData) {
    try {
      // Make sure we're not passing the id again in the data
      const { id, ...dataWithoutId } = itemData;
      const response = await api.put(`/expense-reports/${reportId}/mrl/${itemId}`, dataWithoutId);
      return response.data;
    } catch (error) {
      console.error('Error updating MRL item:', error);
      throw error.response?.data?.message || 'Failed to update MRL item';
    }
  },

  async deleteMRLItem(reportId, itemId) {
    try {
      const response = await api.delete(`/expense-reports/${reportId}/mrl/${itemId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting MRL item:', error);
      throw error.response?.data?.message || 'Failed to delete MRL item';
    }
  },

  async signMRL(reportId, signature) {
    try {
      const response = await api.post(`/expense-reports/${reportId}/mrl/sign`, signature);
      return response.data;
    } catch (error) {
      console.error('Error signing MRL:', error);
      throw error.response?.data?.message || 'Failed to sign MRL';
    }
  },

  // Bank Statements
  async getUserBankStatement(month, year) {
    try {
      const response = await api.get('/bank-statements/user', {
        params: { month, year }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching bank statement:', error);
      throw error.response?.data?.message || 'Failed to fetch bank statement';
    }
  },

  async importBankStatement(reportId) {
    try {
      const response = await api.post(`/expense-reports/${reportId}/import-bank-statement`);
      return response.data;
    } catch (error) {
      console.error('Error importing bank statement:', error);
      throw error.response?.data?.message || 'Failed to import bank statement';
    }
  },

  // FIXED: This is the key method that was failing with 404
  // The issue was that the frontend was adding /api when the base URL already had it
  async getBranchStatements(month, year) {
    try {
      const response = await api.get('/bank-statements/admin/branch', {
        params: { month, year }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching branch statements:', error);
      if (error.response) {
        throw error.response.data?.message || `Server error: ${error.response.status}`;
      } else if (error.request) {
        throw 'No response received from server';
      } else {
        throw error.message;
      }
    }
  },

  async uploadBankStatement(formData) {
    try {
      const response = await api.post('/bank-statements/admin/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading bank statement:', error);
      throw error.response?.data?.message || 'Failed to upload bank statement';
    }
  },

  async deleteBankStatement(id) {
    try {
      const response = await api.delete(`/bank-statements/admin/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting bank statement:', error);
      throw error.response?.data?.message || 'Failed to delete bank statement';
    }
  },

  // Expense Report Admin Review
  async getReportsForReview(params) {
    try {
      // This matches the route in expenseRoutes.js: router.get('/admin/review', ...)
      const response = await api.get('/expense-reports/admin/review', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching reports for review:', error);
      throw error.response?.data?.message || 'Failed to fetch reports for review';
    }
  },

  async getReportForReview(reportId) {
    try {
      const response = await api.get(`/expense-reports/admin/review/${reportId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching report for review:', error);
      throw error.response?.data?.message || 'Failed to fetch report for review';
    }
  },

  async updateGLCodes(reportId, items) {
    try {
      const response = await api.put(`/expense-reports/admin/${reportId}/gl-codes`, { items });
      return response.data;
    } catch (error) {
      console.error('Error updating GL codes:', error);
      throw error;
    }
  },

  // GL Splits API methods
  async createGLSplits(reportId, itemId, splits) {
    try {
      const response = await api.post(`/expense-reports/admin/${reportId}/items/${itemId}/gl-splits`, { splits });
      return response.data;
    } catch (error) {
      console.error('Error creating GL splits:', error);
      throw error;
    }
  },

  async getGLSplits(reportId, itemId) {
    try {
      const response = await api.get(`/expense-reports/admin/${reportId}/items/${itemId}/gl-splits`);
      return response.data;
    } catch (error) {
      console.error('Error getting GL splits:', error);
      throw error;
    }
  },

  async deleteGLSplits(reportId, itemId) {
    try {
      const response = await api.delete(`/expense-reports/admin/${reportId}/items/${itemId}/gl-splits`);
      return response.data;
    } catch (error) {
      console.error('Error deleting GL splits:', error);
      throw error;
    }
  },

  async adminSignReport(reportId, signature) {
    try {
      const response = await api.post(`/expense-reports/admin/${reportId}/sign`, { signature });
      return response.data;
    } catch (error) {
      console.error('Error signing report:', error);
      throw error;
    }
  },

  async generateFinalPDF(reportId) {
    try {
      const response = await api.post(`/expense-reports/admin/${reportId}/final-pdf`);
      return response.data;
    } catch (error) {
      console.error('Error generating final PDF:', error);
      throw error.response?.data?.message || 'Failed to generate final PDF';
    }
  },

  async markSentToBilling(reportId) {
    try {
      const response = await api.post(`/expense-reports/admin/${reportId}/sent-to-billing`);
      return response.data;
    } catch (error) {
      console.error('Error marking report as sent to billing:', error);
      throw error.response?.data?.message || 'Failed to mark report as sent to billing';
    }
  },

  async sendReportToBilling(reportId, billingEmail) {
    try {
      const response = await api.post(`/expense-reports/admin/${reportId}/send-to-billing`, { billingEmail });
      return response.data;
    } catch (error) {
      console.error('Error sending report to billing:', error);
      throw error.response?.data?.message || 'Failed to send report to billing';
    }
  },

  async updateExpenseItem(reportId, itemId, itemData) {
    try {
      const response = await api.put(`/expense-reports/${reportId}/items/${itemId}`, itemData);
      return response.data;
    } catch (error) {
      console.error('Error updating expense item:', error);
      throw error.response?.data?.message || 'Failed to update expense item';
    }
  },

  // Helper method for logging out
  logout() {
    localStorage.removeItem('token');
  },

  async getSupplyOrderReport({ month, year }) {
    try {
      const response = await api.get('/supply/report', {
        params: { month, year }
      });
      return {
        data: response.data.report_data,
        deletion_date: response.data.deletion_date
      };
    } catch (error) {
      console.error('Error getting supply order report:', error);
      throw error;
    }
  },

  async downloadSupplyOrderReport(month, year) {
    try {
      const response = await api.get(`/supply/report/${year}/${month}/download`, {
        responseType: 'blob'
      });
      
      // Create blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `supply-order-${year}-${month}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading supply order report:', error);
      throw error;
    }
  },

  // Admin Maintenance Invoices
  async getMaintenanceInvoices(params) {
    try {
      const response = await api.get('/maintenance-invoices/admin', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching maintenance invoices:', error);
      throw error.response?.data?.message || 'Failed to fetch maintenance invoices';
    }
  },

  async getMaintenanceInvoicePrintUrl(id) {
    try {
      const response = await api.get(`/maintenance-invoices/admin/${id}/print`);
      return response.data.printUrl;
    } catch (error) {
      console.error('Error getting print URL:', error);
      throw error.response?.data?.message || 'Failed to get print URL';
    }
  },

  // Branch Management
  async getBranchUsers(branchName) {
    try {
      const response = await api.get(`/branches/${encodeURIComponent(branchName)}/users`);
      return response.data;
    } catch (error) {
      console.error('Error fetching branch users:', error);
      throw error.response?.data?.message || 'Failed to fetch branch users';
    }
  },

  // User Management
  async getUsersByBranch(branchName) {
    try {
      const response = await api.get(`/admin/users/branch/${encodeURIComponent(branchName)}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching branch users:', error);
      throw error.response?.data?.message || 'Failed to fetch branch users';
    }
  },

  // Checklist Data
  async getChecklistsByMonth(month, year, branch = null) {
    try {
      const url = `/admin/checklists/month/${month}/year/${year}`;
      const params = branch ? { branch } : {};
      const response = await api.get(url, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching checklists by month and year:', error);
      throw error.response?.data?.message || 'Failed to fetch checklists';
    }
  },

  // Expense Reports
  async getExpenseReportsByMonth(month, year) {
    try {
      // Add timestamp to prevent caching
      const timestamp = new Date().getTime();
      const response = await api.get(`/admin/expense-reports/month/${month}/year/${year}?_t=${timestamp}`, {
        headers: {
          'Cache-Control': 'no-cache, no-store',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching expense reports by month and year:', error);
      throw error.response?.data?.message || 'Failed to fetch expense reports';
    }
  },

  // Safety Letters
  async getSafetyLetterSignatures(letterId) {
    try {
      const response = await api.get(`/admin/safety-letters/${letterId}/signatures`);
      return response.data;
    } catch (error) {
      console.error('Error fetching safety letter signatures:', error);
      throw error.response?.data?.message || 'Failed to fetch safety letter signatures';
    }
  },

  // Vehicle Inspections (combined checklists + safety letters)
  async getVehicleInspectionsByMonth(month, year, status = 'all') {
    try {
      // Add timestamp to prevent caching
      const timestamp = new Date().getTime();
      const response = await api.get(`/admin/vehicle-inspections/month/${month}/year/${year}?status=${status}&_t=${timestamp}`, {
        headers: {
          'Cache-Control': 'no-cache, no-store',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching vehicle inspections by month and year:', error);
      throw error.response?.data?.message || 'Failed to fetch vehicle inspections';
    }
  },

  async getBranchVehicles(branchName) {
    try {
      const response = await api.get(`/branches/${encodeURIComponent(branchName)}/vehicles`);
      return response.data;
    } catch (error) {
      console.error('Error fetching branch vehicles:', error);
      throw error.response?.data?.message || 'Failed to fetch branch vehicles';
    }
  },

  async getVehiclesByBranch(branchName) {
    try {
      const response = await api.get('/vehicles', {
        params: { branch: branchName }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching vehicles by branch:', error);
      throw error.response?.data?.message || 'Failed to fetch vehicles';
    }
  },

  async getVehicleByNumber(number) {
    try {
      const response = await api.get(`/vehicles/${encodeURIComponent(number)}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching vehicle:', error);
      throw error.response?.data?.message || 'Failed to fetch vehicle';
    }
  },

  async createVehicle(data) {
    try {
      const response = await api.post('/vehicles', data);
      return response.data;
    } catch (error) {
      console.error('Error creating vehicle:', error);
      throw error.response?.data?.message || 'Failed to create vehicle';
    }
  },

  async updateVehicle(number, data) {
    try {
      const response = await api.put(`/vehicles/${encodeURIComponent(number)}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating vehicle:', error);
      throw error.response?.data?.message || 'Failed to update vehicle';
    }
  },

  async updateVehicleStatus(number, status) {
    try {
      const response = await api.put(`/vehicles/${encodeURIComponent(number)}/status`, { status });
      return response.data;
    } catch (error) {
      console.error('Error updating vehicle status:', error);
      throw error.response?.data?.message || 'Failed to update vehicle status';
    }
  },

  async checkVehicleDuplicates(number, data) {
    try {
      const response = await api.post(`/vehicles/${encodeURIComponent(number)}/check-duplicates`, data);
      return response.data;
    } catch (error) {
      console.error('Error checking vehicle duplicates:', error);
      throw error.response?.data?.message || 'Failed to check duplicates';
    }
  },

  async reassignVehicleData(targetNumber, { conflicts, sourceNumber }) {
    try {
      const response = await api.post(`/vehicles/${encodeURIComponent(targetNumber)}/reassign-data`, {
        conflicts,
        sourceNumber
      });
      return response.data;
    } catch (error) {
      console.error('Error reassigning vehicle data:', error);
      throw error.response?.data?.message || 'Failed to reassign data';
    }
  },

  async assignVehicle(number, { userId, strategy = 'fail_on_conflict', newVehicleNumberForCurrentOwner = null }) {
    try {
      const response = await api.put(`/vehicles/${encodeURIComponent(number)}/assign`, {
        userId,
        strategy,
        newVehicleNumberForCurrentOwner
      });
      return response.data;
    } catch (error) {
      console.error('Error assigning vehicle:', error);
      if (error.response?.status === 409) {
        // Pass through conflict details to UI
        throw error.response.data;
      }
      throw error.response?.data?.message || 'Failed to assign vehicle';
    }
  },

  async transferVehicle(number, { targetBranch, targetUserId = null, makeActive = false, strategy = 'fail_on_conflict', newVehicleNumberForTargetUser = null }) {
    try {
      const response = await api.put(`/vehicles/${encodeURIComponent(number)}/transfer`, {
        targetBranch,
        targetUserId,
        makeActive,
        strategy,
        newVehicleNumberForTargetUser
      });
      return response.data;
    } catch (error) {
      console.error('Error transferring vehicle:', error);
      if (error.response?.status === 409) {
        throw error.response.data;
      }
      throw error.response?.data?.message || 'Failed to transfer vehicle';
    }
  },

  async getUserVehicles() {
    try {
      const response = await api.get('/user/vehicles');
      return response.data;
    } catch (error) {
      console.error('Error fetching user vehicles:', error);
      throw error.response?.data?.message || 'Failed to fetch vehicles';
    }
  },

  createNewExpenseReport: async function(month, year) {
    try {
      const response = await api.post('/expense-reports/new', { month, year });
      return response.data;
    } catch (error) {
      console.error('Error creating new expense report:', error);
      throw error.response?.data?.message || 'Failed to create new expense report';
    }
  },

  // User Impersonation (Service Ops)
  async getServiceOpsUsers() {
    try {
      const response = await api.get('/admin/service-ops/users');
      return response.data;
    } catch (error) {
      console.error('Error fetching Service Ops users:', error);
      throw error.response?.data?.message || 'Failed to fetch Service Ops users';
    }
  },

  async startImpersonation(targetUserId) {
    try {
      const response = await api.post('/admin/impersonation/start', { targetUserId });
      
      // Update the token in localStorage
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        // Update the API instance with the new token
        api.defaults.headers.Authorization = `Bearer ${response.data.token}`;
      }
      
      return response.data;
    } catch (error) {
      console.error('Error starting impersonation:', error);
      throw error.response?.data?.message || 'Failed to start impersonation';
    }
  },

  async stopImpersonation() {
    try {
      const response = await api.post('/admin/impersonation/stop');
      
      // Update the token in localStorage
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        // Update the API instance with the new token
        api.defaults.headers.Authorization = `Bearer ${response.data.token}`;
      }
      
      return response.data;
    } catch (error) {
      console.error('Error stopping impersonation:', error);
      throw error.response?.data?.message || 'Failed to stop impersonation';
    }
  },

  // Admin Activity Logs
  async getAdminActivityLogs(filters = {}) {
    try {
      const params = new URLSearchParams(filters);
      const response = await api.get(`/admin/activity-logs?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching admin activity logs:', error);
      throw error.response?.data?.message || 'Failed to fetch admin activity logs';
    }
  },
};

export default apiService;